/*
 * Factures (consommateurs) et décomptes (producteurs, prosumers) envoyés depuis la todolist :
 * à Accountable (revenue@ / expenses@accountable.eu), par e-mail au membre, ou les deux.
 * Le PDF est le même que celui de l'outil de factures du tableau de bord (InvoiceTemplate).
 * Facturation des consommateurs par canal (Accountable, e-mail, domiciliation) : voir billing.ts.
 * E-mail au membre : coché par défaut pour un membre dont c'est la première période (modèle « Première facture » / « Premier décompte »).
 */
import { supabase } from '../../lib/supabase';
import type { TodoItem } from '../../../supabase/functions/_shared/crmTodo';
import { callFunction } from './api';
import type { InvoiceBillingInfo } from '../InvoiceTemplate';

export type AccountingTarget = 'revenue' | 'expense';
/** Facture : consommateurs (revenu de Sun Is Up). Décompte : producteurs et prosumers (dépense). */
export type DocumentKind = 'invoice' | 'statement';

export const ACCOUNTABLE_ADDRESSES: Record<AccountingTarget, string> = {
  revenue: 'revenue@accountable.eu',
  expense: 'expenses@accountable.eu',
};

export const DOCUMENT_LABELS: Record<DocumentKind, { one: string; many: string; file: string }> = {
  invoice: { one: 'facture', many: 'factures', file: 'Facture' },
  statement: { one: 'décompte', many: 'décomptes', file: 'Decompte' },
};

export interface DocumentMessage { subject: string; body: string }
/** Messages joints : facture, décompte (et leur version pour la première période du membre), facture payée par domiciliation (pré-notification). */
export type MessageKind = DocumentKind | 'first_invoice' | 'first_statement' | 'direct_debit';
export const MESSAGE_KINDS: MessageKind[] = ['invoice', 'first_invoice', 'statement', 'first_statement', 'direct_debit'];
export const MESSAGE_LABELS: Record<MessageKind, string> = {
  invoice: 'Facture',
  first_invoice: 'Première facture',
  statement: 'Décompte',
  first_statement: 'Premier décompte',
  direct_debit: 'Facture – domiciliation',
};

export interface AccountingSettings {
  /** Adresse liée au compte Accountable de Sun Is Up : Accountable n'accepte que les documents envoyés depuis celle-ci. */
  accountableSender: string;
  /** Destinations cochées par défaut dans la fenêtre d'envoi. */
  sendToAccountable: boolean;
  sendToMember: boolean;
  /** Message joint au document envoyé au membre. */
  messages: Record<MessageKind, DocumentMessage>;
}

export const DEFAULT_ACCOUNTING_SETTINGS: AccountingSettings = {
  accountableSender: 'info@sunisup.be',
  sendToAccountable: true,
  sendToMember: true,
  messages: {
    invoice: {
      subject: 'Votre facture Sun Is Up – {{periode}}',
      body: 'Bonjour,\n\nVous trouverez en pièce jointe la facture de l’énergie partagée de {{nom}}.\nPériode : {{periode}}.\n\nMerci d’effectuer le paiement selon les conditions indiquées sur la facture.\n\nBien cordialement,\nL’équipe Sun Is Up',
    },
    first_invoice: {
      subject: 'Bienvenue chez Sun Is Up – votre première facture ({{periode}})',
      body: 'Bonjour,\n\nBienvenue dans la communauté d’énergie Sun Is Up ! Vous trouverez en pièce jointe la première facture de l’énergie partagée de {{nom}}.\nPériode : {{periode}}.\n\nVos prochaines factures seront disponibles dans votre espace membre, avec le détail de votre consommation et de l’énergie partagée.\n\nMerci d’effectuer le paiement selon les conditions indiquées sur la facture.\n\nBien cordialement,\nL’équipe Sun Is Up',
    },
    first_statement: {
      subject: 'Bienvenue chez Sun Is Up – votre premier décompte ({{periode}})',
      body: 'Bonjour,\n\nBienvenue dans la communauté d’énergie Sun Is Up ! Vous trouverez en pièce jointe le premier décompte de l’énergie injectée et partagée de {{nom}}.\nPériode : {{periode}}.\n\nLe montant vous sera versé par virement selon les modalités indiquées sur le décompte. Vos prochains décomptes seront disponibles dans votre espace membre.\n\nBien cordialement,\nL’équipe Sun Is Up',
    },
    statement: {
      subject: 'Votre décompte d’injection Sun Is Up – {{periode}}',
      body: 'Bonjour,\n\nVous trouverez en pièce jointe le décompte de l’énergie injectée et partagée de {{nom}}.\nPériode : {{periode}}.\n\nLe montant vous sera versé par virement selon les modalités indiquées sur le décompte.\n\nBien cordialement,\nL’équipe Sun Is Up',
    },
    direct_debit: {
      subject: 'Votre facture Sun Is Up {{numero_facture}} – prélèvement le {{date_prelevement}}',
      body: 'Bonjour,\n\nVous trouverez en pièce jointe la facture {{numero_facture}} de l’énergie partagée de {{nom}} pour la période {{periode}}.\n\nMontant : {{montant}} €. Il sera prélevé par domiciliation SEPA le {{date_prelevement}} sur le compte {{iban_masque}} (mandat {{reference_mandat}}, identifiant créancier {{identifiant_creancier}}).\n\nVous n’avez rien à faire : merci de ne pas effectuer de virement.\n\nBien cordialement,\nL’équipe Sun Is Up',
    },
  },
};

export const DOCUMENT_VARIABLES: { key: string; label: string }[] = [
  { key: '{{nom}}', label: 'Membre ou société' },
  { key: '{{periode}}', label: 'Période (ex. 3e trimestre 2026)' },
  { key: '{{operation}}', label: 'Opération de partage' },
  { key: '{{numero_facture}}', label: 'Numéro de la facture' },
  { key: '{{montant}}', label: 'Montant TVAC' },
];

/** Variables en plus pour le message « Facture – domiciliation ». */
export const DIRECT_DEBIT_VARIABLES: { key: string; label: string }[] = [
  { key: '{{date_prelevement}}', label: 'Date de prélèvement' },
  { key: '{{iban_masque}}', label: 'IBAN masqué' },
  { key: '{{reference_mandat}}', label: 'Référence du mandat' },
  { key: '{{identifiant_creancier}}', label: 'Identifiant créancier' },
];

export const SENDER_DOMAIN = 'sunisup.be';
export const isAllowedSender = (v: string) => new RegExp(`^[^\\s@<>]+@${SENDER_DOMAIN.replace('.', '\\.')}$`, 'i').test(v.trim());

/* ── Réglages (crm_settings, clé « accounting ») ── */

export async function fetchAccountingSettings(): Promise<AccountingSettings> {
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'accounting').maybeSingle();
  if (error) throw error;
  const v = (data as { value?: Partial<AccountingSettings> } | null)?.value ?? {};
  const d = DEFAULT_ACCOUNTING_SETTINGS;
  return {
    accountableSender: v.accountableSender?.trim() || d.accountableSender,
    sendToAccountable: v.sendToAccountable ?? d.sendToAccountable,
    sendToMember: v.sendToMember ?? d.sendToMember,
    messages: Object.fromEntries(MESSAGE_KINDS.map(k => [k, { ...d.messages[k], ...(v.messages?.[k] ?? {}) }])) as Record<MessageKind, DocumentMessage>,
  };
}

export async function saveAccountingSettings(value: AccountingSettings): Promise<void> {
  if (!isAllowedSender(value.accountableSender)) throw new Error(`L’adresse d’expédition doit se terminer par @${SENDER_DOMAIN}`);
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'accounting', value: { ...value, accountableSender: value.accountableSender.trim().toLowerCase() }, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

/* ── Documents ── */

export const documentKindOf = (item: Pick<TodoItem, 'kind'>): DocumentKind => (item.kind === 'reimbursement' ? 'statement' : 'invoice');

/** Facture ou décompte envoyable depuis la todolist (sociétés et particuliers). */
export const isSendableDocument = (item: TodoItem) =>
  (item.kind === 'invoice' || item.kind === 'reimbursement') && !!item.periodStart;

/** Société (n° BCE) : seule à passer par Accountable ; un particulier reçoit son document par e-mail uniquement. */
export const isCompanyItem = (item: Pick<TodoItem, 'companyNumber'>) => !!item.companyNumber?.replace(/\D/g, '');

/**
 * Catégorie Accountable proposée : décompte → dépenses ; facture d'une société dont tous les EAN sont
 * producteurs ou prosumers → dépenses ; sinon revenus.
 */
export function defaultTarget(item: Pick<TodoItem, 'kind'>, roles: string[]): AccountingTarget {
  if (item.kind === 'reimbursement') return 'expense';
  return roles.length > 0 && roles.every(r => r === 'prosumer' || r === 'producer') ? 'expense' : 'revenue';
}

const digits = (v: string | null | undefined) => (v ?? '').replace(/\D/g, '');

const escapeHtml = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Remplace les variables {{nom}}, {{periode}}… ; les valeurs sont échappées dans le corps (HTML). */
export function renderDocumentMessage(msg: DocumentMessage, vars: Record<string, string>) {
  const values: Record<string, string> = Object.fromEntries(Object.entries(vars).map(([k, v]) => [`{{${k}}}`, v]));
  const apply = (text: string, escape: boolean) =>
    Object.entries(values).reduce((acc, [k, v]) => acc.split(k).join(escape ? escapeHtml(v) : v), text);
  return { subject: apply(msg.subject, false), body: apply(msg.body, true) };
}

export function documentFileName(item: TodoItem, invoiceNumber?: string | null, kind: DocumentKind = documentKindOf(item)): string {
  if (invoiceNumber) return `${DOCUMENT_LABELS[kind].file}_${invoiceNumber}.pdf`;
  const clean = item.title
    .replace(/^groupe\s+/i, '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-]+/g, '_').replace(/^_+|_+$/g, '') || 'societe';
  return `${DOCUMENT_LABELS[documentKindOf(item)].file}_${clean}_${item.periodStart}-${item.periodEnd}.pdf`;
}

export interface DocumentRecipient {
  /** Adresse de facturation (send_email) de la première fiche qui en a une, sinon e-mail du compte. */
  email: string | null;
  /** Types des EAN concernés (consumer, producer, prosumer). */
  roles: string[];
}

export async function fetchDocumentRecipients(items: TodoItem[]): Promise<Record<string, DocumentRecipient>> {
  const ids = Array.from(new Set(items.flatMap(i => i.participantIds)));
  const rows: { id: string; send_email: string | null; email: string | null; type: string | null }[] = [];
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('participants').select('id, send_email, email, type').in('id', ids.slice(i, i + 150));
    if (error) throw error;
    rows.push(...((data ?? []) as typeof rows));
  }
  const byId = new Map(rows.map(r => [r.id, r]));
  const out: Record<string, DocumentRecipient> = {};
  for (const item of items) {
    const list = item.participantIds.map(id => byId.get(id)).filter((r): r is (typeof rows)[number] => !!r);
    out[itemKey(item)] = {
      email: list.find(r => r.send_email?.trim())?.send_email?.trim() ?? list.find(r => r.email?.trim())?.email?.trim() ?? null,
      roles: Array.from(new Set(list.map(r => r.type ?? 'consumer'))),
    };
  }
  return out;
}

export const itemKey = (item: Pick<TodoItem, 'kind' | 'ref' | 'period'>) => `${item.kind}|${item.ref}|${item.period}`;

/* ── Première période du membre ── */

/**
 * Mois d'entrée (« AAAA-MM ») de chaque tâche : date d'entrée la plus ancienne de ses fiches participants.
 * Clé : itemKey. null si aucune date d'entrée.
 */
export async function fetchEntryMonths(items: TodoItem[]): Promise<Record<string, string | null>> {
  const ids = Array.from(new Set(items.flatMap(i => i.participantIds)));
  const entry = new Map<string, string | null>();
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('participants').select('id, entry_date').in('id', ids.slice(i, i + 150));
    if (error) throw error;
    for (const r of (data ?? []) as { id: string; entry_date: string | null }[]) entry.set(r.id, r.entry_date ? String(r.entry_date).slice(0, 7) : null);
  }
  const out: Record<string, string | null> = {};
  for (const item of items) {
    const months = item.participantIds.map(id => entry.get(id)).filter((m): m is string => !!m).sort();
    out[itemKey(item)] = months[0] ?? null;
  }
  return out;
}

/** Première période du membre : son mois d'entrée tombe dans la période facturée (mois facturé = mois de début du partage). */
export const isFirstPeriod = (item: Pick<TodoItem, 'periodStart' | 'periodEnd'>, entryMonth: string | null | undefined) =>
  !!entryMonth && !!item.periodStart && entryMonth >= item.periodStart && entryMonth <= (item.periodEnd || item.periodStart);

/** Modèle proposé : domiciliation (pré-notification) si prélèvement, sinon facture ou décompte, version « première » pour un nouveau membre. */
export function defaultMessageKind(opts: { statement: boolean; first: boolean; directDebit?: boolean }): MessageKind {
  if (opts.directDebit && !opts.statement) return 'direct_debit';
  if (opts.statement) return opts.first ? 'first_statement' : 'statement';
  return opts.first ? 'first_invoice' : 'invoice';
}

export interface BuiltDocument {
  blob: Blob;
  fileName: string;
  /** Clés de invoice_issue_dates, comme l'outil de factures du tableau de bord. */
  groupKey: string | null;
  companyNumber: string | null;
  /** Montant net à payer imprimé sur le PDF (négatif : décompte). */
  netAmount: number;
  /** Numéro imprimé sur le PDF. */
  invoiceNumber: string;
}

/**
 * Génère le PDF de la société (ou du particulier) pour la période de la tâche (facture, ou décompte si le montant net est négatif).
 * billingInfo : numéro figé et mode de paiement de la facture émise depuis la todolist.
 */
/** Fiche participant et filtre société de la tâche (groupe à plusieurs sociétés : une facture par société). */
async function documentSource(item: TodoItem) {
  const { data: participant, error } = await supabase.from('participants').select('*').eq('id', item.participantIds[0]).single();
  if (error || !participant) throw new Error(`Fiche participant introuvable${error ? ` : ${error.message}` : ''}`);
  const { getGroupCompanies } = await import('../InvoiceTemplate');
  let companyFilter: string | null | undefined;
  const groupe = (participant as { groupe?: string | null }).groupe || null;
  if (groupe) {
    const companies = await getGroupCompanies(groupe);
    if (companies.length > 1) {
      const target = digits(item.companyNumber);
      companyFilter = companies.find(c => digits(c.company_number) === target)?.company_number ?? item.companyNumber;
    }
  }
  return { participant, companyFilter, groupe };
}

/** Solde de la période (négatif : en faveur du membre), même calcul que le PDF. */
export async function fetchNetAmount(item: TodoItem): Promise<number> {
  const { participant, companyFilter } = await documentSource(item);
  const { computeInvoiceNetAmount } = await import('../InvoiceTemplate');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const amount = await computeInvoiceNetAmount(participant as any, { startMonth: item.periodStart, endMonth: item.periodEnd }, companyFilter);
  if (amount === null) throw new Error('Solde non calculé');
  return amount;
}

export async function buildDocument(item: TodoItem, issueDate: string, billingInfo?: InvoiceBillingInfo | null): Promise<BuiltDocument> {
  const { participant, companyFilter, groupe } = await documentSource(item);
  const { generateInvoicePDFWithMeta } = await import('../InvoiceTemplate');

  const generated = await generateInvoicePDFWithMeta(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    participant as any, { startMonth: item.periodStart, endMonth: item.periodEnd }, undefined, undefined, companyFilter, issueDate, billingInfo,
  );
  if (!generated) throw new Error('Le PDF n’a pas pu être généré');
  const kind: DocumentKind = generated.netAmount < 0 ? 'statement' : documentKindOf(item);
  return {
    blob: generated.blob,
    fileName: documentFileName(item, billingInfo?.invoiceNumber ?? null, kind),
    groupKey: groupe,
    companyNumber: companyFilter ?? null,
    netAmount: generated.netAmount,
    invoiceNumber: generated.invoiceNumber,
  };
}

export async function downloadBlob(blob: Blob, fileName: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Date d'émission enregistrée pour l'espace membre (même règle que l'outil de factures du tableau de bord). */
export async function saveIssueDate(
  operationId: string, doc: Pick<BuiltDocument, 'groupKey' | 'companyNumber'>, period: { startMonth: string; endMonth: string }, issueDate: string,
): Promise<void> {
  const { data: session } = await supabase.auth.getSession();
  const setBy = session.session?.user?.email ?? null;
  let query = supabase.from('invoice_issue_dates').select('id')
    .eq('sharing_operation_id', operationId).eq('period_start', period.startMonth).eq('period_end', period.endMonth);
  query = doc.groupKey ? query.eq('group_key', doc.groupKey) : query.is('group_key', null);
  query = doc.companyNumber ? query.eq('company_number', doc.companyNumber) : query.is('company_number', null);
  const { data: existing, error } = await query.limit(1);
  if (error) throw error;
  if (existing && existing.length > 0) {
    const { error: e } = await supabase.from('invoice_issue_dates')
      .update({ issue_date: issueDate, set_by_email: setBy, updated_at: new Date().toISOString() }).eq('id', (existing[0] as { id: string }).id);
    if (e) throw e;
  } else {
    const { error: e } = await supabase.from('invoice_issue_dates').insert({
      sharing_operation_id: operationId, group_key: doc.groupKey, company_number: doc.companyNumber,
      period_start: period.startMonth, period_end: period.endMonth, issue_date: issueDate, set_by_email: setBy,
    });
    if (e) throw e;
  }
}

export interface DeliveryResult { requested: boolean; success: boolean; to?: string; error?: string }

/** Envoi par la fonction send-accounting-document (Accountable et/ou membre). */
export async function sendAccountingDocument(params: {
  target: AccountingTarget;
  fileName: string;
  pdfBase64: string;
  accountable: boolean;
  member: { email: string; subject: string; message: string } | null;
}): Promise<{ accountable: DeliveryResult; member: DeliveryResult }> {
  return callFunction('send-accounting-document', params);
}
